<template>
  <div 
    class="relative inline-block text-left"
    @mouseover="showTooltip = true"
    @mouseleave="hideWithDelay"
    @focusin="showTooltip = true"
    @focusout="hideWithDelay"
  >
    <button 
      type="button"
      class="cursor-help ml-1.5 text-gray-400 hover:text-gray-600 transition-colors"
      aria-haspopup="true"
      :aria-expanded="showTooltip ? 'true' : 'false'"
      :aria-label="`Help: ${ariaLabel || 'Click for more information'}`"
      @click.prevent="toggleTooltip"
      ref="triggerRef"
    >
      <Icon :icon="icon" :style="`width: ${iconSize}; height: ${iconSize};`" />
    </button>

    <transition 
      name="tooltip-fade"
      @after-leave="afterLeave"
    >
      <div
        v-if="showTooltip" 
        ref="tooltipRef"
        role="tooltip"
        :class="[
          'absolute z-50 px-3 py-2 text-sm rounded shadow-lg max-w-xs',
          darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800 border border-gray-200',
          positionClasses
        ]"
      >
        <!-- If content is HTML -->
        <div v-if="isHtml" v-html="content"></div>
        
        <!-- If content is plain text -->
        <div v-else>{{ content }}</div>
        
        <!-- Optional illustration image -->
        <img 
          v-if="image" 
          :src="image" 
          :alt="imageAlt || 'Help illustration'" 
          class="mt-2 w-full rounded"
        />
        
        <!-- Optional action button -->
        <div v-if="actionLabel" class="mt-2 text-center">
          <button 
            @click="$emit('action'); showTooltip = false"
            class="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            type="button"
          >
            {{ actionLabel }}
          </button>
        </div>
        
        <!-- Tooltip arrow -->
        <div 
          :class="[
            'tooltip-arrow absolute w-2 h-2 rotate-45',
            darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200',
            arrowPositionClass
          ]"
        ></div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import { useElementSize, useElementBounding } from '@vueuse/core';

const props = defineProps({
  // Content
  content: {
    type: String,
    required: true
  },
  isHtml: {
    type: Boolean,
    default: false
  },
  
  // Visual customization
  icon: {
    type: String,
    default: 'mdi:information-outline'
  },
  iconSize: {
    type: String,
    default: '1em'
  },
  darkMode: {
    type: Boolean,
    default: false
  },
  
  // Positioning
  position: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value)
  },
  
  // Behavior
  showOnClick: {
    type: Boolean,
    default: false
  },
  hideDelay: {
    type: Number,
    default: 300
  },
  
  // Accessibility
  ariaLabel: {
    type: String,
    default: ''
  },
  
  // Optional extras
  image: {
    type: String,
    default: ''
  },
  imageAlt: {
    type: String,
    default: ''
  },
  actionLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['action', 'show', 'hide']);

// State
const showTooltip = ref(false);
const triggerRef = ref(null);
const tooltipRef = ref(null);
let hideTimeoutId = null;

// Calculate position classes based on the prop
const positionClasses = computed(() => {
  switch (props.position) {
    case 'top': return 'bottom-full mb-2';
    case 'right': return 'left-full ml-2';
    case 'bottom': return 'top-full mt-2';
    case 'left': return 'right-full mr-2';
    default: return 'bottom-full mb-2';
  }
});

// Calculate arrow position classes based on the position prop
const arrowPositionClass = computed(() => {
  switch (props.position) {
    case 'top': return 'bottom-[-4px] left-1/2 -translate-x-1/2 border-r border-b';
    case 'right': return 'left-[-4px] top-1/2 -translate-y-1/2 border-l border-t';
    case 'bottom': return 'top-[-4px] left-1/2 -translate-x-1/2 border-l border-t';
    case 'left': return 'right-[-4px] top-1/2 -translate-y-1/2 border-r border-b';
    default: return 'bottom-[-4px] left-1/2 -translate-x-1/2 border-r border-b';
  }
});

// Methods
const toggleTooltip = () => {
  if (props.showOnClick) {
    showTooltip.value = !showTooltip.value;
    if (showTooltip.value) emit('show');
    else emit('hide');
  }
};

const hideWithDelay = () => {
  if (hideTimeoutId) clearTimeout(hideTimeoutId);
  hideTimeoutId = setTimeout(() => {
    showTooltip.value = false;
    emit('hide');
  }, props.hideDelay);
};

const afterLeave = () => {
  emit('hide');
};

// Handle clicks outside to close the tooltip
const handleClickOutside = (event) => {
  if (
    showTooltip.value &&
    props.showOnClick &&
    tooltipRef.value &&
    triggerRef.value &&
    !tooltipRef.value.contains(event.target) &&
    !triggerRef.value.contains(event.target)
  ) {
    showTooltip.value = false;
    emit('hide');
  }
};

// Watch for changes and set up event listeners
onMounted(() => {
  if (props.showOnClick) {
    document.addEventListener('click', handleClickOutside);
  }
});

onBeforeUnmount(() => {
  if (hideTimeoutId) clearTimeout(hideTimeoutId);
  if (props.showOnClick) {
    document.removeEventListener('click', handleClickOutside);
  }
});

// Expose methods for parent components
defineExpose({
  show: () => {
    showTooltip.value = true;
    emit('show');
  },
  hide: () => {
    showTooltip.value = false;
    emit('hide');
  }
});
</script>

<style scoped>
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style> 